import React, { useState } from "react";
import { useSOC } from "../context/SOCContext";
import { DataTable } from "../../common/DataTable";
import ConfirmModal from "../ui/ConfirmModal";
import ApiLoader from "../../ui/ApiLoader";
import { RESPONSE_ACTION_TYPES, RESPONSE_STATUSES } from "../data/mockData";

const Response = () => {
  const { responses, incidents, triggerResponseAction, updateResponseActionStatus, loading, errors } = useSOC();
  const [pending, setPending] = useState(null); // { actionType }
  const [target, setTarget] = useState("");
  const [incidentId, setIncidentId] = useState("");

  const uniqueTargets = [...new Set(responses.map((r) => r.target).filter(Boolean))];

  const columns = [
    {
      id: "id",
      accessorFn: (r) => r.id,
      header: "Action ID",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Action ID", placeholder: "Search action ID" } },
      cell: ({ row }) => <span>{row.original.id}</span>,
    },
    {
      id: "action",
      accessorFn: (r) => r.action,
      header: "Action",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Action",
          placeholder: "All actions",
          options: RESPONSE_ACTION_TYPES.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <span>{row.original.action}</span>,
    },
    {
      id: "target",
      accessorFn: (r) => r.target,
      header: "Target",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Target",
          placeholder: "All targets",
          options: uniqueTargets.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <span>{row.original.target}</span>,
    },
    {
      id: "triggeredBy",
      accessorFn: (r) => r.triggeredBy,
      header: "Triggered By",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Triggered By", placeholder: "Search triggered by" } },
      cell: ({ row }) => <span>{row.original.triggeredBy}</span>,
    },
    {
      id: "time",
      accessorFn: (r) => r.time,
      header: "Time",
      enableSorting: false,
      cell: ({ row }) => <span>{new Date(row.original.time).toLocaleString()}</span>,
    },
    {
      id: "status",
      accessorFn: (r) => r.status,
      header: "Status",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Status",
          placeholder: "All statuses",
          options: RESPONSE_STATUSES.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => (
        <select
          value={row.original.status}
          onChange={(e) => updateResponseActionStatus(row.original.id, e.target.value)}
          className="text-xs bg-gray-50 border border-gray-200 rounded-lg px-2 py-1"
        >
          {RESPONSE_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      ),
    },
    {
      id: "relatedIncidentId",
      accessorFn: (r) => r.relatedIncidentId,
      header: "Related Incident",
      enableSorting: false,
      cell: ({ row }) => <span>{row.original.relatedIncidentId}</span>,
    },
  ];

  const confirmAction = () => {
    const incident = incidents.find((i) => i.id === incidentId) || null;
    triggerResponseAction(pending, target || incident?.affectedAssetName || "Manual target", incident);
    setPending(null);
    setTarget("");
    setIncidentId("");
  };

  if (loading.responses && responses.length === 0) {
    return <ApiLoader variant="spinner" label="Loading Response Actions..." className="py-20" />;
  }

  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-gray-800 mb-1">Trigger Response Action</h3>
        <p className="text-xs text-gray-400 mb-4">
          Selecting an action below creates a real response action record for review. No production system integration is performed yet.
        </p>
        <div className="flex flex-wrap gap-2">
          {RESPONSE_ACTION_TYPES.map((action) => (
            <button
              key={action}
              onClick={() => setPending(action)}
              className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 hover:border-gray-300"
            >
              {action}
            </button>
          ))}
        </div>
      </div>

      <DataTable
        columns={columns}
        data={responses}
        getRowId={(r) => String(r.id)}
        emptyMessage={errors.responses || "No records found"}
        enableColumnVisibility={false}
        enableColumnResizing
        enableColumnFilters
        maxBodyHeight="65vh"
      />

      <ConfirmModal
        open={!!pending}
        title={`Confirm: ${pending}`}
        confirmLabel="Trigger Action"
        message={
          <span>
            This creates a real response action record for tracking. It does not affect any production system yet.
            Provide a target (asset or user) and optionally link it to an incident.
            <span className="block mt-3 space-y-2">
              <input
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                placeholder="Target (e.g. SRV-001)"
                className="w-full px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700"
              />
              <select
                value={incidentId}
                onChange={(e) => setIncidentId(e.target.value)}
                className="w-full px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700"
              >
                <option value="">No related incident</option>
                {incidents.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.id} - {i.name}
                  </option>
                ))}
              </select>
            </span>
          </span>
        }
        onConfirm={confirmAction}
        onCancel={() => {
          setPending(null);
          setTarget("");
          setIncidentId("");
        }}
      />
    </div>
  );
};

export default Response;
